import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import PercentAndSqft from './PercentAndSqft';
import Skeleton from '@mui/material/Skeleton';
import makeStyles from '@mui/styles/makeStyles';
import IconButton from '@mui/material/IconButton';
import { RowContainer } from './ZoneRowComponent';
import { MoreOptionDialog } from '@blackhyve/common';
import ColorDropdown from 'components/ColorDropdown';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DragIndicator from '@mui/icons-material/DragIndicator';
import DeleteIconComponent from '@blackhyve/common/src/icons/Delete';
import PercentIconComponent from '@blackhyve/common/src/icons/Percent';
import SqftIconComponent from '@blackhyve/common/src/icons/SqftIcon';
import InlineEditCardTitle from 'components/common/v3/InlineEditCardTitle';
import AreaPolygonIconComponent from '@blackhyve/common/src/icons/AreaPolygon';
import {
  createArea,
  deleteArea,
  selectAreaById,
  updateArea,
  removeEmptyArea,
} from '../store/areasSlice';

import DisplayWithEditAccess, { EditContext } from 'components/common/v3/DisplayWithEditAccess';

const useMuiSlider = makeStyles((theme) => ({
  root: {
    color: '#03DAC6',
    height: '8px',
    padding: '0px',
  },
  thumb: {
    '&:hover': {
      boxShadow: '0px 0px 0px 3px rgb(63 81 181 / 16%)',
    },
    color: '#00696B',
  },
  track: {
    color: '#00696B',
    zIndex: 2,
  },
  rail: {
    color: '#03DAC6',
  },
}));

const AreaRowComponent = ({ id, dragHandleProps }) => {
  const dispatch = useDispatch();

  const sliderClasses = useMuiSlider();

  const canEdit = useContext(EditContext);

  const name = useSelector((state) => selectAreaById(state, id)?.name);
  const priority = useSelector((state) => selectAreaById(state, id)?.priority);
  const zoneId = useSelector((state) => selectAreaById(state, id)?.zone_id);
  const density = useSelector((state) => selectAreaById(state, id)?.density);
  const percent = useSelector((state) => selectAreaById(state, id)?.percent);
  const sqft = useSelector((state) => selectAreaById(state, id)?.sqft);
  const color = useSelector((state) => selectAreaById(state, id)?.color);
  const createdAt = useSelector((state) => selectAreaById(state, id)?.created_at);

  const [loading, setLoading] = useState(false);
  const [densityState, setDensityState] = useState(density);

  const [openMoreOptionDialog, setOpenMoreOptionDialog] = useState(null);
  const [openPercentAndSqftDialog, setOpenPercentAndSqftDialog] = useState({});
  const requestParameters = {
    name: name,
    id: id,
    density,
    percent,
    sqft,
  };

  const handleDelete = () => {
    setLoading(true);
    if (createdAt) {
      dispatch(deleteArea({ id: id })).then(() => {
        setLoading(false);
      });
    } else {
      dispatch(removeEmptyArea({ id, zoneId }));
      setLoading(false);
    }
  };

  const optionMenu = [
    {
      name: 'Delete Area',
      icon: <DeleteIconComponent color="red" />,
      action: handleDelete,
      style: { color: 'red' },
    },
  ];

  const handleOpenMoreOptionDialog = (event) => {
    setOpenMoreOptionDialog(event.target);
  };

  const handleCloseMoreOptionDialog = () => {
    setOpenMoreOptionDialog(false);
  };

  const handleOpenPercentAndSqftDialog = (event, tab) => {
    setOpenPercentAndSqftDialog({ anchorEl: event.target, tab });
  };

  const handleClosePercentAndSqftDialog = () => {
    setOpenPercentAndSqftDialog({});
  };

  const handleUpdate = (requestParameters) => {
    setLoading(true);
    if (id < 0) {
      dispatch(createArea({ zoneId, ...requestParameters })).then(() => {
        setLoading(false);
      });
    } else {
      dispatch(updateArea(requestParameters)).then(() => {
        setLoading(false);
      });
    }
  };

  const handleSaveColor = (selectedColor) => {
    setLoading(true);
    dispatch(updateArea({ id, color: selectedColor })).then(() => setLoading(false));
  };

  useEffect(() => setDensityState(density), [density]);

  return (
    <RowContainer container>
      {loading ? (
        <Skeleton className="skeleton-row" variant="text" />
      ) : (
        <>
          <Grid container item alignItems={'center'} xs={7}>
            <Grid item {...dragHandleProps}>
              <DragIndicator sx={{ color: 'grey.600' }} />
            </Grid>
            <Grid item>
              <AreaPolygonIconComponent
                style={{
                  marginLeft: '40px',
                  padding: '5px',
                  display: 'block',
                }}
              />
            </Grid>
            <Grid item className="card-title">
              <InlineEditCardTitle
                fieldName={'name'}
                handleUpdate={handleUpdate}
                isDefaultEdit={id < 0}
                isEditable={canEdit}
                requestParameters={requestParameters}
                textToDisplay={name}
              />
            </Grid>
          </Grid>
          <Grid container item xs={5}>
            <Grid item className="flex" justifyContent="center" xs={2}>
              <Grid
                item
                style={{
                  display: 'flex',
                  marginLeft: '10px',
                  marginRight: '10px',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ColorDropdown
                  color={color}
                  disabled={!canEdit}
                  handleSaveColor={handleSaveColor}
                />
              </Grid>
            </Grid>
            <Grid item className="value-text" style={{ paddingLeft: '30px' }} xs={2}>
              {priority}
            </Grid>
            <Grid item className={'value-text'} xs={2}>
              <Button onClick={(event) => handleOpenPercentAndSqftDialog(event, 1)}>
                {percent ? (
                  <>
                    <PercentIconComponent />
                    {parseFloat(percent * 100).toFixed(2)}
                  </>
                ) : (
                  '-'
                )}
              </Button>
            </Grid>

            <Grid item className={'value-text'} xs={3}>
              <Button onClick={(event) => handleOpenPercentAndSqftDialog(event, 2)}>
                {sqft ? (
                  <>
                    <SqftIconComponent />
                    {sqft}
                  </>
                ) : (
                  '-'
                )}
              </Button>
            </Grid>
            <Grid item className="value-text" xs={2}>
              <Slider
                disabled={!canEdit}
                max={100}
                min={0}
                size={'small'}
                step={1}
                value={Number((densityState * 100).toFixed(2))}
                valueLabelDisplay="auto"
                classes={{
                  root: sliderClasses.root,
                  thumb: sliderClasses.thumb,
                  track: sliderClasses.track,
                  rail: sliderClasses.rail,
                }}
                onChange={(event, newValue) => setDensityState(Number((newValue / 100).toFixed(2)))}
                onChangeCommitted={() => handleUpdate({ id, density: densityState })}
              />
            </Grid>
            <Grid item className="value-text" style={{ justifyContent: 'flex-end' }} xs={1}>
              <DisplayWithEditAccess>
                <IconButton
                  disableFocusRipple={true}
                  disableRipple={true}
                  size="small"
                  style={{ padding: 0 }}
                  onClick={handleOpenMoreOptionDialog}
                >
                  <MoreHorizIcon className={'moreHoriz'} size="small" />
                </IconButton>
                {openMoreOptionDialog && (
                  <MoreOptionDialog
                    anchorEl={openMoreOptionDialog}
                    handleClose={handleCloseMoreOptionDialog}
                    id="more-option-popover"
                    open={openMoreOptionDialog ? true : false}
                    optionMenu={optionMenu}
                  />
                )}
              </DisplayWithEditAccess>
            </Grid>
          </Grid>
        </>
      )}
      {openPercentAndSqftDialog && openPercentAndSqftDialog.anchorEl && (
        <PercentAndSqft
          anchorEl={openPercentAndSqftDialog.anchorEl}
          defaultTab={openPercentAndSqftDialog.tab}
          handleClose={handleClosePercentAndSqftDialog}
          zoneId={zoneId}
        />
      )}
    </RowContainer>
  );
};

export default AreaRowComponent;
